import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import MediaRelationsService from '../../services/mediarelations.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { Stack, Box, LinearProgress } from '@mui/material';
import { FluxButton } from '@meltwater/flux-web-components-react';
import csvDownload from 'json-to-csv-export';
import styles from './mediarelations.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { useMediaRelationsData } from './mediarelations';
import DataTable from '../../components/DataTable';

const Newswire = ({ load, setActiveTab }) => {
    const [mediarelationspermissions, setMediarelationspermissions] = useState([]);
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [severity, setSeverity] = useState('info');
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    const { companyId } = useCompanyId();
    const { 
        newswireData, 
        setNewswireData,
        setLoading,
        apiAttempts,
        markApiAttempt,
        resetApiAttempt
    } = useMediaRelationsData();
    
    const loadingTimerRef = useRef(null);
    const landingPageNavRef = useRef(false);
    const isRequestInProgressRef = useRef(false);
    const isMountedRef = useRef(true);
    
    // Custom guide steps specific to Newswire
    const newswireGuideSteps = useMemo(() => [
        {
            title: 'Newswire Audit',
            text: 'This guide will help you navigate the Newswire features.',
            element: null,
        },
        {
            title: 'Search Newswire',
            text: 'Use the search box to quickly find newswire distributions by headline or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export Audit Sheet',
            text: 'You can export all newswire audit data or selected items using these buttons.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Row Selection',
            text: 'Select rows with checkboxes to perform operations on specific newswire distributions.',
            element: '.mrt-row-select-checkbox',
            position: { top: 100, left: 30 }
        },
        {
            title: 'Refresh Data',
            text: 'Use this button to refresh newswire data from the server.',
            element: 'button[title="Refresh Data"]',
            position: { top: 0, left: 300 }
        },
    ], []);

    // Track initial load to identify direct navigation from landing page
    useEffect(() => {
        if (load && !landingPageNavRef.current) {
            console.log("Newswire tab activated first time from landing page");
            landingPageNavRef.current = true;
        }
    }, [load]);

    // Fetch data function separated for reuse
    const fetchData = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isRequestInProgressRef.current) {
            console.log("Request already in progress, skipping duplicate call");
            return;
        }

        try {
            isRequestInProgressRef.current = true;
            setIsLoading(true);
            setLoading(true);
            setLoadingMessage('Fetching newswire data...');
            loadingTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
                }
            }, 15000);
            
            console.log("Initiating getNewswires call for company:", companyId);
            const response = await MediaRelationsService.getNewswires(companyId);
            console.log("getNewswires call successful");
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setNewswireData(response.data);
                setFeedbackMsg(`Successfully loaded ${response.data.length} newswire distributions.`);
                setSeverity('success');
                setOpenSnackBar(true);
                setHasError(false);
                
                // Mark that we've successfully fetched data
                markApiAttempt('newswire');
            }
            
            return response.data;
        } catch (error) {
            console.error("Error in getNewswires API call:", error);
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setOpenSnackBar(true);
                const errorMsg = error.message || 'Unknown error';
                setFeedbackMsg(`Error loading data: ${errorMsg}, Please refresh or contact support.`);
                setErrorMessage(errorMsg);
                setSeverity('error');
                setHasError(true);
            }
            throw error;
        } finally {
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            if (isMountedRef.current) {
                setLoadingMessage('');
                setIsLoading(false);
                setLoading(false);
            }
        }
    }, [companyId, setLoading, setNewswireData, markApiAttempt]);

    // Effect for initial setup
    useEffect(() => {
        const jwtToken = getJwtToken();
        setIsFetching(false);
        
        if (jwtToken) {
            const { mediarelationspermissions } = jwt_decode(jwtToken);
            setMediarelationspermissions(mediarelationspermissions);
        }

        // Track component mount status
        isMountedRef.current = true;
        
        return () => {
            isMountedRef.current = false;
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, []);

    // Separate effect for data loading
    useEffect(() => {
        if (!load || !companyId) {
            return;
        }

        async function loadData() {
            // Check if we have cached data
            if (newswireData) {
                console.log("Using cached newswire data");
                setIsLoading(false);
                return;
            }
            
            // Check if this component has already attempted to load data and had an error
            if (hasError && apiAttempts.newswire) {
                console.log("Skipping API call due to previous error");
                return;
            }
            
            // Prevent multiple requests
            if (isRequestInProgressRef.current) {
                console.log("Request already in progress, skipping");
                return;
            }
            
            // Fetch new data if needed
            try {
                console.log("No cached data found, fetching fresh data");
                await fetchData();
            } catch (error) {
                console.error("Error loading newswire data:", error);
            }
        }
        
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, companyId, newswireData, hasError, apiAttempts.newswire]);

    const resetErrorState = () => {
        console.log("Resetting newswire error state to allow retry");
        setHasError(false);
        resetApiAttempt('newswire'); // Reset the API attempt tracking
    };

    const refreshData = async () => {
        // Prevent multiple refresh calls
        if (isRequestInProgressRef.current) {
            console.log("Refresh already in progress, skipping");
            return;
        }

        try {
            resetApiAttempt('newswire'); // Reset API attempt for refresh
            setHasError(false); // Clear any previous errors
            setLoadingMessage('Refreshing newswire data...');
            await fetchData();
            if (isMountedRef.current) {
                setFeedbackMsg('Data refreshed successfully');
                setSeverity('success');
                setOpenSnackBar(true);
            }
        } catch (error) {
            console.error("Error refreshing newswire data:", error);
            if (isMountedRef.current) {
                const errorMsg = error.message || 'Unknown error';
                setErrorMessage(errorMsg);
                setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
                setSeverity('error');
                setOpenSnackBar(true);
            }
        }
    };
    
    const exportAuditSheet = async (rows) => {
        const exportdata = [];

        rows.forEach(row => {
            row = row.original;
            row.distributions[0].entitlements.forEach(credit => {
                exportdata.push({
                    id: row._id,
                    headline: row.contents[0].headline,
                    pubDate: row.scheduleTime || row.lastStateTime || row.updatedAt,
                    creditType: credit.key,
                    creditsUsed: credit.value,
                    circuitNames: row.circuitNames,
                    isDeleted: row.isDeleted === true ? "Yes" : "No"
                });
            });
        });

        const data = {
          data: exportdata,
          delimiter: ',',
          filename: `Newswire Audit for ${companyId}`,
        };
    
        csvDownload(data);
    };

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    };

    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    tint="primary"
                    type="filled"
                    onClick={() => exportAuditSheet(table.getPrePaginationRowModel().rows)}
                >
                    Export All Audit Sheet
                </FluxButton>
                <FluxButton
                    tint="accent"
                    type="filled"
                    onClick={() => exportAuditSheet(table.getSelectedRowModel().rows)}
                    disabled={
                        !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                    }
                >
                    Export Selected Audit Sheet
                </FluxButton>
                <FluxButton
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
            </Stack>
        );
    };

    const columns = useMemo(
        () => [
            {
                header: 'id',
                accessorKey: "_id",
                enableGlobalFilter: true
            },
            {
                header: 'headline',
                size: 500,
                accessorFn: (row) => row.contents[0].headline,
                enableGlobalFilter: true
            },
            {
                header: 'pubDate',
                accessorFn: (row) => row.scheduleTime || row.lastStateTime || row.updatedAt,
                enableGlobalFilter: false
            },
            {
                header: 'creditTypes',
                accessorFn: (row) => row.distributions[0].entitlements.map(ct => ct.key).join(", "),
                enableGlobalFilter: true
            },
            {
                header: 'creditsUsed',
                accessorFn: (row) => row.distributions[0].entitlements.map(ct => ct.value).join(", "),
                enableGlobalFilter: false
            },
            {
                header: 'circuitNames',
                accessorFn: (row) => row.circuitNames.join(", "),
                enableGlobalFilter: true
            },
            {
                header: 'isDeleted',
                accessorFn: (row) => row.isDeleted === true ? "Yes" : "No",
                enableGlobalFilter: true
            }
        ],
        [],
    );

    if (!mediarelationspermissions || !mediarelationspermissions.includes('read')) {
        return <div>You do not have permission to access this page.</div>;
    }

    return (
        <div className={styles.Search}>
                {isFetching && (
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress color="secondary" />
                    </Box>
                )}

                <DataTable 
                    columns={columns}
                    data={newswireData || []}
                    isLoading={isLoading && !hasError}
                    loadingMessage={loadingMessage || 'Loading newswire data...'}
                    hasError={hasError}
                    errorMessage={errorMessage}
                    errorTitle="Error Loading Newswire Data"
                    onBackClick={() => setActiveTab(null)}
                    onTryAgain={() => {
                        resetErrorState();
                        refreshData();
                    }}
                    renderToolbarActions={renderToolbarActions}
                    backTooltip="Return to Dashboard"
                    showGuide={false}
                    guideSteps={newswireGuideSteps}
                    snackbarProps={{
                        open: openSnackBar,
                        onClose: handleCloseSnackBar,
                        message: feedbackMsg,
                        severity: severity,
                        autoHideDuration: 10000
                    }}
                    tableProps={{
                        enableRowSelection: true,
                        enableSelectAll: true,
                        muiSearchTextFieldProps: {
                            placeholder: 'Search newswire...',
                            variant: 'standard',
                            sx: { width: '300px' }
                        },
                        initialState: { 
                            density: 'compact',
                            columnVisibility: { "_id": false },
                            pagination: { pageSize: 50 }
                        }
                    }}
                />
        </div>
    );
};

export default Newswire;
